/**
 * Card Component
 * Versatile card for features, pricing, and content display
 */

import React from 'react';
import './Card.css';

/**
 * @param {Object} props
 * @param {'default'|'elevated'|'accent'|'featured'} [props.variant='default'] - Visual variant
 * @param {'compact'|'default'|'spacious'} [props.padding='default'] - Padding size
 * @param {boolean} [props.centered=false] - Center align content
 * @param {boolean} [props.clickable=false] - Add hover effects for clickable cards
 * @param {React.ReactNode} props.children - Card content
 */
function Card({
    variant = 'default',
    padding = 'default',
    centered = false,
    clickable = false,
    children,
    className = '',
    ...props
}) {
    const classNames = [
        'card',
        `card--${variant}`,
        padding !== 'default' && `card--${padding}`,
        centered && 'card--centered',
        clickable && 'card--clickable',
        className
    ].filter(Boolean).join(' ');

    return (
        <div className={classNames} {...props}>
            {children}
        </div>
    );
}

/**
 * Card Icon subcomponent
 */
function CardIcon({ children }) {
    return <div className="card__icon">{children}</div>;
}

/**
 * Card Badge subcomponent
 */
function CardBadge({ children }) {
    return <span className="card__badge">{children}</span>;
}

/**
 * Card Title subcomponent
 */
function CardTitle({ as: Component = 'h3', children }) {
    return <Component className="card__title">{children}</Component>;
}

/**
 * Card Description subcomponent
 */
function CardDescription({ children }) {
    return <p className="card__description">{children}</p>;
}

/**
 * Card Footer subcomponent
 */
function CardFooter({ children }) {
    return <div className="card__footer">{children}</div>;
}

// Attach subcomponents
Card.Icon = CardIcon;
Card.Badge = CardBadge;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Footer = CardFooter;

export default Card;
